import React, { useState, useContext } from 'react';
import styled from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import supabase from '../../config/supabaseClient';
import { Button, Input } from '../common/UIComponents';
import { AuthContext } from '../../contexts/AuthContext';

const AuthContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: linear-gradient(135deg, var(--background) 0%, var(--surface) 100%);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    width: 1000px;
    height: 1000px;
    border-radius: 50%;
    background: linear-gradient(45deg, var(--primary-light) 0%, var(--primary) 100%);
    top: -500px;
    right: -300px;
    opacity: 0.1;
    animation: pulse 8s ease-in-out infinite;
  }

  &::after {
    content: '';
    position: absolute;
    width: 800px;
    height: 800px;
    border-radius: 50%;
    background: linear-gradient(45deg, var(--primary) 0%, var(--primary-dark) 100%);
    bottom: -400px;
    left: -200px;
    opacity: 0.1;
    animation: pulse 8s ease-in-out infinite reverse;
  }

  @keyframes pulse {
    0% { transform: scale(1); }
    50% { transform: scale(1.1); }
    100% { transform: scale(1); }
  }
`;

const AuthCard = styled.div`
  background: var(--surface);
  padding: 40px;
  border-radius: 20px;
  box-shadow: 0 8px 30px var(--card-shadow);
  width: 100%;
  max-width: 440px;
  position: relative;
  z-index: 1;
  backdrop-filter: blur(10px);
  border: 1px solid var(--border);
  animation: slideUp 0.5s ease-out;

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const Logo = styled(Link)`
  font-size: 32px;
  font-weight: 800;
  color: var(--text-primary);
  text-decoration: none;
  display: block;
  text-align: center;
  margin-bottom: 32px;
  background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  letter-spacing: -1px;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 12px;
  text-align: center;
  color: var(--text-primary);
`;

const Description = styled.p`
  font-size: 16px;
  color: var(--text-secondary);
  text-align: center;
  margin-bottom: 32px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  color: var(--text-secondary);
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const Required = styled.span`
  color: var(--error);
  font-size: 18px;
  line-height: 1;
`;

const SignupButton = styled(Button)`
  width: 100%;
  padding: 14px;
  margin-top: 8px;
  font-size: 16px;
  justify-content: center;
`;

const LoginPrompt = styled.p`
  font-size: 14px;
  color: var(--text-secondary);
  text-align: center;
  margin-top: 24px;
`;

const LoginLink = styled(Link)`
  color: var(--primary);
  font-weight: 600;
  text-decoration: none;

  &:hover {
    color: var(--primary-dark);
    text-decoration: underline;
  }
`;

const ErrorMessage = styled.div`
  color: var(--error);
  background: ${props => `${props.theme.error}10`};
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;

  &::before {
    content: '⚠️';
  }
`;

const SuccessMessage = styled.div`
  color: var(--success);
  background: ${props => `${props.theme.success}10`};
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  margin-top: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: pre-line;

  &::before {
    content: '✅';
  }
`;

const PasswordRequirements = styled.ul`
  list-style: none;
  padding: 0;
  margin: 4px 0 0;
  font-size: 12px;
  color: var(--text-tertiary);
`;

const Requirement = styled.li`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 2px;

  &::before {
    content: '•';
    color: var(--primary);
  }
`;

const Select = styled.select`
  padding: 14px;
  border: 1px solid var(--border);
  border-radius: 12px;
  font-size: 16px;
  background: var(--surface);
  color: var(--text-primary);
  transition: all var(--transition-normal);

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: var(--shadow-md);
  }
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: var(--text-secondary);
  cursor: pointer;

  a {
    color: var(--primary);
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

const SignUp = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { setUser } = useContext(AuthContext);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Basic validation
      if (!formData.email || !formData.password || !formData.confirmPassword) {
        throw new Error('Please fill in all fields');
      }

      if (formData.password !== formData.confirmPassword) {
        throw new Error('Passwords do not match');
      }

      // Sign up with Supabase
      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            email: formData.email,
            created_at: new Date().toISOString(),
            is_pro: false,
            subscription_status: 'free'
          }
        }
      });

      if (signUpError) throw signUpError;

      // Sign in immediately after signup
      const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
        email: formData.email,
        password: formData.password
      });

      if (signInError) throw signInError;

      // Set user in context
      setUser(signInData.user);

      // Redirect to dashboard with replace to prevent going back to signup
      navigate('/dashboard', { replace: true });

    } catch (error) {
      console.error('Error signing up:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContainer>
      <AuthCard>
        <Logo to="/">nexpend.</Logo>
        <Title>Create Account</Title>
        <Description>Join thousands of users managing their subscriptions with Nexpend</Description>

        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>Email <Required>*</Required></Label>
            <Input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="Enter your email"
            />
          </FormGroup>

          <FormGroup>
            <Label>Password <Required>*</Required></Label>
            <Input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              minLength="6"
              placeholder="Enter your password"
            />
            <PasswordRequirements>
              <Requirement>At least 6 characters long</Requirement>
              <Requirement>Contains at least one number</Requirement>
              <Requirement>Contains at least one special character</Requirement>
            </PasswordRequirements>
          </FormGroup>

          <FormGroup>
            <Label>Confirm Password <Required>*</Required></Label>
            <Input
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              minLength="6"
              placeholder="Confirm your password"
            />
          </FormGroup>

          {error && <ErrorMessage>{error}</ErrorMessage>}

          <SignupButton type="submit" disabled={loading}>
            {loading ? 'Creating Account...' : 'Create Account'}
          </SignupButton>
        </Form>

        <LoginPrompt>
          Already have an account? <LoginLink to="/login">Login</LoginLink>
        </LoginPrompt>
      </AuthCard>
    </AuthContainer>
  );
};

export default SignUp; 